import { type Href, router, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { FullScreenSpinner, useToastController } from 'smoothie';

import LoginEmail from '../../../auth/components/email/LoginEmail';
import LoginOTP from '../../../auth/components/otp/LoginOTP';
import { useBiometricAuthentication } from '../../../auth/hooks/useBiometricAuthentication';
import { useUser } from '../../../user/providers/UserProvider';

export default function Verify() {
    const { callback, ...forwardParams } = useLocalSearchParams();
    const toast = useToastController();
    const { authenticateBiometrics, hasBiometricFactor } =
        useBiometricAuthentication();
    const [fallbackToOTP, setFallbackToOTP] = useState(false);
    const [fallbackToPassword, setFallbackToPassword] = useState(false);
    const { user } = useUser();

    const onSuccess = useCallback(() => {
        router.navigate({
            // @ts-expect-error - TODO expo upgrade: better typed routes
            pathname: callback as Href,
            params: forwardParams,
        });
    }, [callback, forwardParams]);

    const onError = useCallback(() => {
        toast.show('Biometric authentication failed');
        router.back();
    }, [toast]);

    const handleFallback = useCallback(() => {
        if (user?.disableMfa) {
            setFallbackToPassword(true);
        } else {
            setFallbackToOTP(true);
        }
    }, [user]);

    useEffect(() => {
        if (hasBiometricFactor !== undefined) {
            if (hasBiometricFactor) {
                void authenticateBiometrics(onSuccess, onError);
            } else {
                handleFallback();
            }
        }
    }, [hasBiometricFactor, authenticateBiometrics, onSuccess, onError]);

    const headerMessage = callback?.includes('/change-pin')
        ? 'For security purposes, identity verification is required to modify your PIN.'
        : 'For security purposes, identity verification is required.';

    if (fallbackToOTP) {
        return (
            <LoginOTP
                onSuccess={onSuccess}
                headerMessage={headerMessage}
                overrideIsSmsEnabled={true}
                includeRememberDevice={false}
            />
        ); // [ENG-2830]
    }

    if (fallbackToPassword) {
        return (
            <LoginEmail
                onSuccess={onSuccess}
                headerMessage={headerMessage}
                includeSignupLink={false}
            />
        );
    }

    return <FullScreenSpinner />;
}
